import type { ButtonHTMLAttributes, FC, ReactNode } from 'react'

import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'gold' | 'outline' | 'ghost' | 'danger' | 'light'

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon'

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-navy text-navy-foreground hover:bg-navy/90 shadow-sm',
    gold: 'bg-gold text-gold-foreground hover:bg-gold/90 shadow-sm',
    outline: 'border border-input bg-card text-foreground hover:bg-sand',
    ghost: 'text-foreground hover:bg-sand',
    danger: 'bg-destructive text-white hover:bg-destructive/90 shadow-sm',
    light: 'bg-white/95 text-navy hover:bg-white shadow-sm'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-9 px-3.5 text-sm gap-1.5',
    md: 'h-11 px-5 text-sm gap-2',
    lg: 'h-12 px-7 text-base gap-2',
    icon: 'size-10'
}

export const buttonClass = ({ variant = 'primary', size = 'md', className }: { variant?: ButtonVariant, size?: ButtonSize, className?: string } = {}): string => cn(
    'inline-flex shrink-0 items-center justify-center rounded-full font-semibold whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-55',
    VARIANTS[variant],
    SIZES[size],
    className
)

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    loading?: boolean
    children?: ReactNode
}

export const Button: FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled,
    className,
    type = 'button',
    children,
    ...rest
}): ReactNode => {
    return (
        <button
            type={type}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            className={buttonClass({ variant, size, className })}
            {...rest}
        >
            {loading && <Loader2 className='size-4 animate-spin' aria-hidden='true' />}
            {children}
        </button>
    )
}
